import { defineComponent as _defineComponent } from 'vue'
import { PropType } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useRoute, watch } from '@nuxtjs/composition-api'
import { useRestaurantModel } from '@entities/restaurant'
import { useThemeModel } from '@entities/theme'
import { useMenuModel } from './../model'
import { IProduct, ITransformLabels, ILabel, IModifier } from './../model/types'
import { amplitudeLogEvent, amplitudeEvents } from '@shared/lib'
import { isCartDisabledForTable } from '@entities/restaurant/utils/cartRules'


export default /*#__PURE__*/_defineComponent({
  __name: 'MenuProduct',
  props: {
  product: {
    type: Object as PropType<IProduct>,
    required: true,
    default: () => {}
  }
},
  emits: ['setDetailProduct'],
  setup(__props, { emit }) {

const props = __props





const themeModel = useThemeModel()
const restaurantModel = useRestaurantModel()
const menuModel = useMenuModel()
const route = useRoute()

const labels = computed(() => {
  const arr = []
  for (const key in props.product.labels) {
    const label = key as keyof ITransformLabels
    const labelData = props.product.labels[label]
    if (typeof labelData !== 'boolean' && labelData.status) {
      arr.push(props.product.labels[label] as ILabel)
    }
  }
  return arr
})

const allModifiersMap = computed(() => {
  const map = new Map<number, IModifier>()
  props.product.modifiers?.forEach((modifier) => {
    map.set(modifier.id, modifier)
  })
  props.product.modifier_groups?.forEach((group) => {
    group.modifiers?.forEach((modifier) => {
      map.set(modifier.id, modifier)
    })
  })
  return map
})

const selectedModifiersIds = computed(() => {
  return menuModel.selectedModifiersMap[props.product.id] || []
})

const totalPrice = computed(() => {
  let price = props.product.price
  selectedModifiersIds.value.forEach((modifierId) => {
    const modifier = allModifiersMap.value.get(modifierId)
    if (modifier) {
      price += modifier.price
    }
  })
  return price
})

const productPictureSrc = computed(() => {
  const versions = props.product.product_picture?.versions
  return versions?.optimized || versions?.lazyLoad || versions?.original || ''
})

const isPhotoBlockVisible = computed(() => {
  if (restaurantModel.code === 'myata-lounge-perovo-30879') {
    return Boolean(productPictureSrc.value)
  }
  return true
})

const ingredients = computed(() => {
  const ingredients = props.product.ingredients
  return ingredients ? ingredients.replace(/\n/g, '<br/>') : null
})

const COMPOSITION_RESTAURANT_CODES = ['iiko2', 'myata-lounge-perovo-30879']

const isCompositionVisible = computed(() => (
  COMPOSITION_RESTAURANT_CODES.includes(restaurantModel.code)
))

const INGREDIENTS_COLLAPSED_MAX_HEIGHT = 32
const INGREDIENTS_TRANSITION_DURATION = 300

const isIngredientsExpanded = ref(false)
const isIngredientsCollapsing = ref(false)
const ingredientsWrapRef = ref<HTMLElement | null>(null)
const ingredientsRef = ref<HTMLElement | null>(null)
const canToggleIngredients = ref(false)
const compositionMaxHeight = ref(INGREDIENTS_COLLAPSED_MAX_HEIGHT)

const isIngredientsExpandedState = () => (
  isIngredientsExpanded.value || isIngredientsCollapsing.value
)

const isChevronExpanded = () => (
  isIngredientsExpanded.value && !isIngredientsCollapsing.value
)

const getCompositionWrapStyle = () => ({
  maxHeight: `${compositionMaxHeight.value}px`,
  overflow: 'hidden',
  transition: `max-height ${INGREDIENTS_TRANSITION_DURATION}ms ease`
})

const getCompositionExpandedClass = () => ({
  'menu-product__composition--expanded': isIngredientsExpandedState()
})

const getCompositionMoreClass = () => ({
  'menu-product__composition-more--expanded': isIngredientsExpandedState()
})

const getChevronStyle = () => ({
  transform: isChevronExpanded() ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: `transform ${INGREDIENTS_TRANSITION_DURATION}ms ease`
})

const measureCompositionHeight = () => {
  if (!ingredientsWrapRef.value) {
    return INGREDIENTS_COLLAPSED_MAX_HEIGHT
  }

  return ingredientsWrapRef.value.scrollHeight
}

const setExpandedHeight = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      if (isIngredientsCollapsing.value) {
        return
      }
      compositionMaxHeight.value = measureCompositionHeight()
    })
  })
}

const isIngredientsToggleVisible = computed(() => canToggleIngredients.value)

let collapseFinishTimer: ReturnType<typeof setTimeout> | null = null

const clearCollapseFinishTimer = () => {
  if (collapseFinishTimer) {
    clearTimeout(collapseFinishTimer)
    collapseFinishTimer = null
  }
}

const finishCollapse = () => {
  if (!isIngredientsCollapsing.value) {
    return
  }

  clearCollapseFinishTimer()
  isIngredientsExpanded.value = false
  isIngredientsCollapsing.value = false
  compositionMaxHeight.value = INGREDIENTS_COLLAPSED_MAX_HEIGHT
  observeIngredientsElement()
  checkIngredientsOverflow()
}

const scheduleCollapseFinish = () => {
  clearCollapseFinishTimer()
  collapseFinishTimer = setTimeout(
    finishCollapse,
    INGREDIENTS_TRANSITION_DURATION + 50
  )
}

const checkIngredientsOverflow = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      if (isIngredientsCollapsing.value) {
        return
      }

      if (isIngredientsExpanded.value) {
        setExpandedHeight()
        return
      }

      const el = ingredientsRef.value
      if (!el) {
        return
      }

      canToggleIngredients.value = el.scrollHeight > INGREDIENTS_COLLAPSED_MAX_HEIGHT + 1
    })
  })
}

let ingredientsResizeObserver: ResizeObserver | null = null

const observeIngredientsElement = () => {
  if (!ingredientsResizeObserver) {
    return
  }

  ingredientsResizeObserver.disconnect()

  const el = ingredientsWrapRef.value || ingredientsRef.value
  if (el) {
    ingredientsResizeObserver.observe(el)
  }
}

onMounted(() => {
  if (typeof ResizeObserver !== 'undefined') {
    ingredientsResizeObserver = new ResizeObserver(checkIngredientsOverflow)
    observeIngredientsElement()
  }

  checkIngredientsOverflow()
})

onBeforeUnmount(() => {
  clearCollapseFinishTimer()
  ingredientsResizeObserver?.disconnect()
  ingredientsResizeObserver = null
})

watch(ingredients, () => {
  if (isIngredientsExpanded.value) {
    setExpandedHeight()
  } else {
    compositionMaxHeight.value = INGREDIENTS_COLLAPSED_MAX_HEIGHT
  }
  checkIngredientsOverflow()
  observeIngredientsElement()
})
watch(() => props.product.id, () => {
  clearCollapseFinishTimer()
  isIngredientsExpanded.value = false
  isIngredientsCollapsing.value = false
  canToggleIngredients.value = false
  compositionMaxHeight.value = INGREDIENTS_COLLAPSED_MAX_HEIGHT
  checkIngredientsOverflow()
})

const toggleIngredients = () => {
  if (isIngredientsCollapsing.value) {
    return
  }

  if (isIngredientsExpanded.value) {
    isIngredientsCollapsing.value = true
    ingredientsResizeObserver?.disconnect()
    compositionMaxHeight.value = measureCompositionHeight()

    nextTick(() => {
      requestAnimationFrame(() => {
        compositionMaxHeight.value = INGREDIENTS_COLLAPSED_MAX_HEIGHT
      })
    })

    scheduleCollapseFinish()
    return
  }

  isIngredientsExpanded.value = true
  compositionMaxHeight.value = INGREDIENTS_COLLAPSED_MAX_HEIGHT
  setExpandedHeight()
}

const handleCompositionTransitionEnd = (event: TransitionEvent) => {
  if (event.target !== ingredientsWrapRef.value || event.propertyName !== 'max-height') {
    return
  }

  finishCollapse()
}

const getProductWeight = (elem: IProduct) => {
  let result = ''
  if (elem.count && elem.count > 0) {
    result = `${elem.count}`

    if (elem.unit) { result += `&nbsp;${elem.unit}` }
  }

  if (elem.nutrition_info.total.calories && elem.nutrition_info.show_total) {
    result += ` ${elem.nutrition_info.total.calories}&nbsp;ккал`
  }
  return result
}

const isAddControlsVisible = computed(() => {
  const tableId = restaurantModel.tableCode || route.value.params.id
  const isDisabled = isCartDisabledForTable(restaurantModel.code, tableId)
  return restaurantModel.isCartEnabled && !isDisabled
})

const openDetailProduct = () => {
  if (restaurantModel.code === 'klevo-sochi-morskoi-port-21040') {
    return
  }
  amplitudeLogEvent(amplitudeEvents.CHOICE_ITEM_FOR_CART)
  emit('setDetailProduct', props.product)
}


return { __sfc: true,props, emit, themeModel, restaurantModel, menuModel, route, labels, allModifiersMap, selectedModifiersIds, totalPrice, productPictureSrc, isPhotoBlockVisible, ingredients, COMPOSITION_RESTAURANT_CODES, isCompositionVisible, INGREDIENTS_COLLAPSED_MAX_HEIGHT, INGREDIENTS_TRANSITION_DURATION, isIngredientsExpanded, isIngredientsCollapsing, ingredientsWrapRef, ingredientsRef, canToggleIngredients, compositionMaxHeight, isIngredientsExpandedState, isChevronExpanded, getCompositionWrapStyle, getCompositionExpandedClass, getCompositionMoreClass, getChevronStyle, measureCompositionHeight, setExpandedHeight, isIngredientsToggleVisible, collapseFinishTimer, clearCollapseFinishTimer, finishCollapse, scheduleCollapseFinish, checkIngredientsOverflow, ingredientsResizeObserver, observeIngredientsElement, toggleIngredients, handleCompositionTransitionEnd, getProductWeight, isAddControlsVisible, openDetailProduct }
}

})